import React, { useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  User,
  Phone,
  Shield,
  Globe,
  Bell,

} from "lucide-react";
import { motion } from "framer-motion";
import { Switch } from "../../../components/ui/Switch";
import styles from "./MyProfilePage.module.css";
import { jwtDecode } from "jwt-decode";
import cacheStorage from "../../../utils/cacheStorage";

export function MyProfilePage() {
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(() => {
    let userName = "Admin User";
    let userPicture = cacheStorage.getItem("user_picture") || "";
    let userPhone = cacheStorage.getItem("user_phone") || "";

    try {
      const token = cacheStorage.getItem("id_token");

      if (token) {
        const decoded = jwtDecode(token);

        userName =
            decoded.name ||
            decoded.nickname ||
            decoded.email?.split("@")[0] ||
            "Admin User";

        userPicture =
            userPicture ||
            decoded.picture ||
            "";

        userPhone =
            userPhone ||
            decoded["https://SmartCargo/phone"] ||
            decoded["https://smartcargo.com/phone"] ||
            decoded.phone_number ||
            decoded.phone ||
            decoded.user_metadata?.phone ||
            "";
      }
    } catch (error) {
      console.error("Failed to decode token", error);
    }

    return {
      fullName: userName,
      picture: userPicture,
      phone: userPhone,
    };
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: false,
    systemNotifications: false,
    deliveryUpdates: true,
    paymentAlerts: true,
    systemAlerts: false,
    courierUpdates: true,
    twoFactorAuth: false,
  });


  return (
    <div className={styles['profile-page']}>
      <div className={styles['profile-header']}>
        <h1 className={styles['profile-title']}>{t.myProfileTitle}</h1>
        <p className={styles['profile-subtitle']}>{t.myProfileSubtitle}</p>
      </div>

      <div className={styles['profile-grid']}>
        {/* Left Column - Profile Summary */}
        <div className={styles['profile-left-column']}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={styles['profile-card']}
          >
            <div className={styles['profile-avatar-section']}>
              <div className={styles['profile-avatar']}>
                {formData.picture ? (
                    <img
                        src={formData.picture}
                        alt={formData.fullName}
                        className={styles['profile-avatar-image']}
                    />
                ) : (
                    formData.fullName.charAt(0).toUpperCase()
                )}
              </div>
              <h2 className={styles['profile-name']}>
                {formData.fullName}
              </h2>
              <span className={styles['profile-badge']}>
                {t.admin}
              </span>

            </div>

            <div className={styles['profile-info-section']}>
              <div className={styles['profile-info-row']}>
                <span className={styles['profile-info-label']}>{t.languagePreference}:</span>
                <span className={styles['profile-info-value']}>ENG</span>
              </div>
            </div>

            <div className={styles['profile-actions-section']}>
              <button
                className={styles['btn-edit-profile']}
                onClick={() => setIsEditing(true)}
              >
                {t.editProfile}
              </button>
              <button className={styles['btn-change-password']}>
                {t.changePassword}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Detailed Settings */}
        <div className={styles['profile-right-column']}>
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={styles['profile-card']}
          >
            <div className={styles['card-header']}>
              <h3 className={styles['card-title']}>
                <User className={styles['card-icon']} />
                {t.personalInformation}
              </h3>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={styles['btn-text-edit']}
              >
                {isEditing ? t.cancel : "Edit"}
              </button>
            </div>

            <div className={styles['form-grid']}>
              <div className={styles['form-group']}>
                <label className={styles['form-label']}>
                  {t.fullName}
                </label>
                <div className={`${styles['input-container']} ${isEditing ? styles['editing'] : ''}`}>
                  <User className={styles['input-icon']} />
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    disabled={!isEditing}
                    className={styles['form-input']}
                  />
                </div>
              </div>



              <div className={styles['form-group']}>
                <label className={styles['form-label']}>
                  {t.phone}
                </label>
                <div className={`${styles['input-container']} ${isEditing ? styles['editing'] : ''}`}>
                  <Phone className={styles['input-icon']} />
                  <input
                      type="tel"
                      value={formData.phone || ""}
                      placeholder="+994 50 123 45 67"
                      onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                      }
                      disabled={!isEditing}
                      className={styles["form-input"]}
                  />
                </div>
              </div>


            </div>

            {isEditing && (
              <div className={styles['form-actions']}>
                <button
                    className={styles["btn-save"]}
                    onClick={() => {
                      cacheStorage.setItem("user_phone", formData.phone || "");
                      cacheStorage.setItem("user_name", formData.fullName || "");
                      setIsEditing(false);
                    }}
                >
                  {t.save}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className={styles['btn-cancel']}
                >
                  {t.cancel}
                </button>
              </div>
            )}
          </motion.div>

          {/* Security Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={styles['profile-card']}
          >
            <h3 className={`${styles['card-title']} ${styles['card-title-margin']}`}>
              <Shield className={styles['card-icon']} />
              {t.securitySettings}
            </h3>

            <div className={styles['security-settings-content']}>
              <div className={styles['two-factor-auth']}>
                <div>
                  <p className={styles['setting-label']}>{t.twoFactorAuth}</p>
                  <p className={styles['setting-description']}>
                    Add an extra layer of security
                  </p>
                </div>
                <Switch
                  checked={preferences.twoFactorAuth}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, twoFactorAuth: checked })
                  }
                />
              </div>

              <button className={styles['btn-logout-devices']}>
                {t.logoutAllDevices}
              </button>
            </div>
          </motion.div>

          {/* Preferences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={styles['profile-card']}
          >
            <h3 className={`${styles['card-title']} ${styles['card-title-margin']}`}>
              <Globe className={styles['card-icon']} />
              {t.preferences}
            </h3>

            <div className={styles['preferences-content']}>
              <div className={styles['preference-item']}>
                <span className={styles['setting-label']}>
                  {t.emailNotifications}
                </span>
                <Switch
                  checked={preferences.emailNotifications}
                  onCheckedChange={(checked) =>
                    setPreferences({
                      ...preferences,
                      emailNotifications: checked,
                    })
                  }
                />
              </div>

              <div className={styles['preference-item']}>
                <span className={styles['setting-label']}>
                  {t.systemNotifications}
                </span>
                <Switch
                  checked={preferences.systemNotifications}
                  onCheckedChange={(checked) =>
                    setPreferences({
                      ...preferences,
                      systemNotifications: checked,
                    })
                  }
                />
              </div>
            </div>
          </motion.div>

          {/* Notification Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={styles['profile-card']}
          >
            <h3 className={`${styles['card-title']} ${styles['card-title-margin']}`}>
              <Bell className={styles['card-icon']} />
              {t.notificationSettings}
            </h3>

            <div className={styles['notification-settings-list']}>
              <div className={styles['notification-item']}>
                <span className={styles['setting-label-navy']}>{t.deliveryUpdates}</span>
                <Switch
                  checked={preferences.deliveryUpdates}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, deliveryUpdates: checked })
                  }
                />
              </div>

              <div className={styles['notification-item']}>
                <span className={styles['setting-label-navy']}>{t.paymentAlerts}</span>
                <Switch
                  checked={preferences.paymentAlerts}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, paymentAlerts: checked })
                  }
                />
              </div>

              <div className={styles['notification-item']}>
                <span className={styles['setting-label-navy']}>{t.systemAlerts}</span>
                <Switch
                  checked={preferences.systemAlerts}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, systemAlerts: checked })
                  }
                />
              </div>

              <div className={`${styles['notification-item']} ${styles['no-border']}`}>
                <span className={styles['setting-label-navy']}>{t.courierUpdates}</span>
                <Switch
                  checked={preferences.courierUpdates}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, courierUpdates: checked })
                  }
                />
              </div>
            </div>
          </motion.div>


        </div>
      </div>
    </div>
  );
}